import React from 'react';
import Icon from '../../../components/AppIcon';

const OpenSourceContributionsSection = () => {
  const contributions = [
    {
      project: "TensorFlow",
      role: "Core Contributor",
      contributions: "2.3M+ lines of code",
      impact: "Performance optimizations for enterprise workloads",
      stars: "185k",
      icon: "Brain"
    },
    {
      project: "PyTorch",
      role: "Maintainer",
      contributions: "1.8M+ lines of code",
      impact: "MLOps integration and deployment tools",
      stars: "82k",
      icon: "Zap"
    },
    {
      project: "Kubernetes",
      role: "AI/ML SIG Lead",
      contributions: "850k+ lines of code",
      impact: "AI workload orchestration and scaling",
      stars: "110k",
      icon: "Server"
    },
    {
      project: "Apache Airflow",
      role: "Committer",
      contributions: "650k+ lines of code",
      impact: "ML pipeline automation and monitoring",
      stars: "36k",
      icon: "GitBranch"
    },
    {
      project: "Hugging Face Transformers",
      role: "Contributor",
      contributions: "420k+ lines of code",
      impact: "Enterprise model deployment optimizations",
      stars: "133k",
      icon: "Bot"
    },
    {
      project: "MLflow",
      role: "Core Contributor",
      contributions: "380k+ lines of code",
      impact: "Model lifecycle management enhancements",
      stars: "18k",
      icon: "BarChart3"
    }
  ];

  const communityStats = [
    {
      icon: "Code2",
      value: "12M+",
      label: "Lines of Code Contributed",
      description: "Across 50+ open-source projects"
    },
    {
      icon: "Users",
      value: "2.5M+",
      label: "Developers Impacted",
      description: "Through our open-source contributions"
    },
    {
      icon: "Star",
      value: "150k+",
      label: "GitHub Stars",
      description: "On projects we maintain or contribute to"
    },
    {
      icon: "GitPullRequest",
      value: "5,200+",
      label: "Pull Requests",
      description: "Merged into major AI/ML projects"
    }
  ];

  const thoughtLeadership = [
    {
      title: "The Future of Enterprise AI: Why Open Source Wins",
      type: "Keynote",
      event: "AI Summit 2024",
      audience: "2,500+ attendees",
      icon: "Mic"
    },
    {
      title: "Building AI Independence: A Strategic Imperative",
      type: "Whitepaper",
      event: "Harvard Business Review",
      audience: "500k+ readers",
      icon: "FileText"
    },
    {
      title: "Open Source AI: The Path to Technological Sovereignty",
      type: "Panel Discussion",
      event: "TechCrunch Disrupt",
      audience: "10k+ viewers",
      icon: "Users"
    },
    {
      title: "MLOps at Scale: Lessons from 500+ Implementations",
      type: "Technical Talk",
      event: "KubeCon + CloudNativeCon",
      audience: "8k+ developers",
      icon: "Code"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-brand-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Heart" size={16} className="text-brand-primary" />
            <span className="text-sm font-medium text-brand-primary">Open-Source Leadership</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
            Deep Community
            <span className="block text-brand-primary">Involvement & Impact</span>
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            We don't just use open-source AI—we help build it. Our team's contributions shape the tools that power the future of enterprise AI.
          </p>
        </div>

        {/* Major Contributions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {contributions?.map((contribution, index) => (
            <div key={index} className="bg-white rounded-xl shadow-executive p-6 hover:shadow-executive-lg transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={contribution?.icon} size={24} className="text-brand-primary" />
                </div>
                <div className="flex items-center space-x-1 text-sm text-text-secondary">
                  <Icon name="Star" size={14} />
                  <span>{contribution?.stars}</span>
                </div>
              </div>
              
              <h3 className="text-lg font-bold text-text-primary mb-2">
                {contribution?.project}
              </h3>
              
              <div className="text-sm text-brand-primary font-medium mb-3">
                {contribution?.role}
              </div>
              
              <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                {contribution?.impact}
              </p>
              
              <div className="flex items-center space-x-2">
                <Icon name="Code2" size={14} className="text-brand-accent" />
                <span className="text-sm font-medium text-text-primary">
                  {contribution?.contributions}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Community Statistics */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-executive mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              Community Impact by the Numbers
            </h3>
            <p className="text-lg text-text-secondary">
              Our contributions reach millions of developers worldwide
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {communityStats?.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat?.icon} size={32} className="text-brand-primary" />
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-brand-primary mb-2">
                  {stat?.value}
                </div>
                <div className="text-text-primary font-medium mb-2">
                  {stat?.label}
                </div>
                <p className="text-text-secondary text-sm">
                  {stat?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Thought Leadership */}
        <div className="bg-gradient-to-br from-brand-primary to-blue-800 rounded-2xl p-8 lg:p-12 text-white">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              Thought Leadership & Speaking
            </h3>
            <p className="text-blue-100 text-lg">
              Sharing insights and shaping the future of enterprise AI
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {thoughtLeadership?.map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-brand-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={item?.icon} size={20} className="text-brand-accent" />
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-white mb-2">
                      {item?.title}
                    </h4>
                    
                    <div className="flex items-center space-x-4 text-blue-200 text-sm mb-2">
                      <span className="bg-brand-accent/20 px-2 py-1 rounded">
                        {item?.type}
                      </span>
                      <span>{item?.event}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-blue-100 text-sm">
                      <Icon name="Users" size={14} />
                      <span>{item?.audience}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OpenSourceContributionsSection;